import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import type { Locale } from '@connaro/i18n';
import { Menu, PanelLeft, PanelLeftClose, Search, Star, X } from 'lucide-react';
import { useAuth } from '../auth/AuthProvider';
import { CommandPalette } from './CommandPalette';
import { ErrorBoundary } from './ErrorBoundary';
import { Logo } from './Logo';
import { NavSidebar } from './NavSidebar';
import { toggleFavorite, useFavorites } from '../hooks/useFavorites';
import { recordRecent } from '../hooks/useRecent';
import { itemForPath } from '../navigation/registry';
import { useI18n } from '../i18n/I18nProvider';
import { usePreferences } from '../preferences/PreferencesProvider';

const COLLAPSE_KEY = 'connaro.sidebar.collapsed';
const MOBILE_QUERY = '(max-width: 860px)';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Tracks the mobile breakpoint so the drawer can render an always-expanded nav. */
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MOBILE_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = (): void => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isMobile;
}

export function Layout(): JSX.Element {
  const { me, logout } = useAuth();
  const { t, locale, locales, setLocale } = useI18n();
  const { mode, toggleMode } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();

  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, String(collapsed));
    } catch {
      // Storage may be unavailable (private mode); the preference just won't persist.
    }
  }, [collapsed]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  // Close the drawer whenever we leave mobile, so it can't linger off-screen.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const [paletteOpen, setPaletteOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (e.key === 'Escape') {
        setDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Track recently visited routes for the command palette (empty-query view).
  useEffect(() => {
    recordRecent(location.pathname);
  }, [location.pathname]);

  const favoriteIds = useFavorites();
  const currentItem = itemForPath(location.pathname);
  const isFavorite = currentItem ? favoriteIds.includes(currentItem.id) : false;

  const onLogout = async (): Promise<void> => {
    await logout();
    navigate('/login');
  };

  // On mobile the drawer always shows the full, expanded nav — the collapse rail is
  // a desktop affordance only.
  const railCollapsed = collapsed && !isMobile;

  return (
    <div
      className={`app-shell${railCollapsed ? ' app-shell--collapsed' : ''}${
        drawerOpen ? ' app-shell--drawer-open' : ''
      }`}
    >
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      {drawerOpen && (
        <div
          className="nav-drawer-backdrop"
          role="presentation"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <aside className="sidebar" aria-label="Primary navigation">
        <div className="brand">
          {/* The official Connaro lockup (transparent, for the dark sidebar) when
              expanded; the mark alone in the collapsed rail. */}
          {railCollapsed ? (
            <Logo />
          ) : (
            <img className="brand__lockup" src="/brand/connaro-logo-dark.png" alt="Connaro" />
          )}
          <button
            type="button"
            className="sidebar__collapse"
            onClick={() => setCollapsed((c) => !c)}
            aria-pressed={collapsed}
            aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
            title={collapsed ? t('nav.expand') : t('nav.collapse')}
          >
            {collapsed ? (
              <PanelLeft size={18} aria-hidden="true" />
            ) : (
              <PanelLeftClose size={18} aria-hidden="true" />
            )}
          </button>
          <button
            type="button"
            className="sidebar__close"
            onClick={() => setDrawerOpen(false)}
            aria-label={t('nav.closeMenu')}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>
        <NavSidebar
          collapsed={railCollapsed}
          onExpand={() => setCollapsed(false)}
          onNavigate={() => setDrawerOpen(false)}
        />
      </aside>
      <div className="content">
        <header className="topbar">
          <button
            type="button"
            className="topbar__menu"
            onClick={() => setDrawerOpen(true)}
            aria-label={t('nav.openMenu')}
            aria-expanded={drawerOpen}
          >
            <Menu size={20} aria-hidden="true" />
          </button>
          <button
            type="button"
            className="cmdk-trigger"
            onClick={() => setPaletteOpen(true)}
            aria-keyshortcuts="Meta+K Control+K"
            aria-label={t('palette.trigger')}
          >
            <Search size={16} aria-hidden="true" />
            <span className="cmdk-trigger__label">{t('palette.trigger')}</span>
            <kbd className="cmdk-kbd">⌘K</kbd>
          </button>
          {currentItem && (
            <button
              type="button"
              className={`topbar__fav${isFavorite ? ' is-fav' : ''}`}
              onClick={() => toggleFavorite(currentItem.id)}
              aria-pressed={isFavorite}
              aria-label={isFavorite ? t('nav.unpin') : t('nav.pin')}
              title={isFavorite ? t('nav.unpin') : t('nav.pin')}
            >
              <Star size={18} fill={isFavorite ? 'currentColor' : 'none'} aria-hidden="true" />
            </button>
          )}
          <div className="topbar__right">
            <button
              type="button"
              className="btn btn--ghost"
              onClick={toggleMode}
              aria-pressed={mode === 'advanced'}
              title={t('mode.hint')}
            >
              {mode === 'advanced' ? t('mode.advanced') : t('mode.basic')}
            </button>
            <label className="locale">
              <span className="visually-hidden">Language</span>
              <select
                value={locale}
                onChange={(e) => setLocale(e.target.value as Locale)}
                aria-label="Language"
              >
                {locales.map((l) => (
                  <option key={l} value={l}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
            <span className="user">{me?.displayName}</span>
            <button type="button" className="btn btn--ghost" onClick={() => void onLogout()}>
              {t('auth.signOut')}
            </button>
          </div>
        </header>
        <main id="main" className="main">
          {/* Keyed by path so navigating to another page clears a crashed one. */}
          <ErrorBoundary
            key={location.pathname}
            labels={{
              title: t('errors.pageTitle'),
              intro: t('errors.pageIntro'),
              reload: t('errors.reload'),
            }}
          >
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
